import React, { useEffect, useMemo, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faStore,
  faRotateRight,
  faPlus,
  faPen,
  faTrash,
  faXmark,
} from '@fortawesome/free-solid-svg-icons';
import { jwtDecode } from 'jwt-decode';
import {
  createWhatsAppCatalogProduct,
  deleteWhatsAppCatalogProduct,
  getMyInfo,
  getToken,
  getUsers,
  getWhatsAppCatalogProducts,
  getWhatsAppCatalogs,
  updateWhatsAppCatalogProduct,
  uploadWhatsAppCatalogImage,
} from '../../../lib/api';
import './admin.css';

const emptyProductForm = {
  retailer_id: '',
  name: '',
  description: '',
  price: '',
  currency: 'INR',
  availability: 'in stock',
  condition: 'new',
  image_url: '',
  url: '',
  brand: '',
};

const AVAILABILITY_OPTIONS = [
  'in stock',
  'out of stock',
  'preorder',
  'available for order',
  'discontinued',
];

const CONDITION_OPTIONS = ['new', 'refurbished', 'used'];

const parsePriceForForm = (price) => {
  if (price == null || price === '') return '';
  const raw = String(price).trim();
  const currencyMatch = raw.match(/\s([A-Z]{3})$/i);
  const currency = currencyMatch ? currencyMatch[1].toUpperCase() : '';
  const numeric = raw.replace(/[^\d.]/g, '');
  return { price: numeric, currency };
};

const productToForm = (p) => {
  const parsed = parsePriceForForm(p.price);
  return {
    retailer_id: p.retailer_id || '',
    name: p.name || '',
    description: p.description || '',
    price: typeof parsed === 'object' ? parsed.price : '',
    currency: (typeof parsed === 'object' && parsed.currency) || p.currency || 'INR',
    availability: p.availability || 'in stock',
    condition: p.condition || 'new',
    image_url: p.image_url || '',
    url: p.url || '',
    brand: p.brand || '',
  };
};

const resolveOwnPhoneNumberId = async () => {
  const token = getToken();
  if (token) {
    try {
      const decoded = jwtDecode(token);
      if (decoded?.phone_number_id) return String(decoded.phone_number_id);
    } catch {
      /* fall through */
    }
  }
  const data = await getMyInfo();
  const numberId = data?.userInfo?.number_id;
  return numberId ? String(numberId) : '';
};

/**
 * @param {{ mode?: 'admin' | 'user' }} props
 * admin — pick any account; user — manage own catalogue only
 */
const ManageCatalogue = ({ mode = 'admin' }) => {
  const isUserMode = mode === 'user';
  const [users, setUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [ownPhoneNumberId, setOwnPhoneNumberId] = useState('');
  const [accountMeta, setAccountMeta] = useState(null);
  const [catalogs, setCatalogs] = useState([]);
  const [selectedCatalogId, setSelectedCatalogId] = useState('');
  const [manualCatalogId, setManualCatalogId] = useState('');
  const [warnings, setWarnings] = useState([]);
  const [commerceSettings, setCommerceSettings] = useState(null);
  const [products, setProducts] = useState([]);
  const [nextCursor, setNextCursor] = useState(null);
  const [form, setForm] = useState(emptyProductForm);
  const [editingProductId, setEditingProductId] = useState(null);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [loadingUsers, setLoadingUsers] = useState(!isUserMode);
  const [loadingAccount, setLoadingAccount] = useState(isUserMode);
  const [loadingCatalogs, setLoadingCatalogs] = useState(false);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [deletingId, setDeletingId] = useState('');
  const [uploadingImage, setUploadingImage] = useState(false);
  const [localPreviewUrl, setLocalPreviewUrl] = useState('');

  const selectedUser = useMemo(
    () => users.find((u) => String(u.id) === String(selectedUserId)),
    [users, selectedUserId]
  );

  const selectedCatalog = useMemo(
    () => catalogs.find((c) => String(c.id) === String(selectedCatalogId)),
    [catalogs, selectedCatalogId]
  );

  const phoneNumberId = isUserMode
    ? ownPhoneNumberId
    : selectedUser?.phone_number_id
      ? String(selectedUser.phone_number_id)
      : '';

  useEffect(() => {
    if (!isUserMode) return undefined;
    let cancelled = false;
    const loadOwnAccount = async () => {
      setLoadingAccount(true);
      setError('');
      try {
        const numberId = await resolveOwnPhoneNumberId();
        if (cancelled) return;
        if (!numberId) {
          setError('Could not resolve your WhatsApp phone number ID. Re-login and try again.');
          setOwnPhoneNumberId('');
          return;
        }
        setOwnPhoneNumberId(numberId);
        try {
          const info = await getMyInfo();
          if (!cancelled) setAccountMeta(info?.userInfo || null);
        } catch {
          if (!cancelled) setAccountMeta(null);
        }
      } catch (e) {
        if (!cancelled) {
          setError(e?.response?.data?.message || e?.message || 'Failed to load account');
        }
      } finally {
        if (!cancelled) setLoadingAccount(false);
      }
    };
    loadOwnAccount();
    return () => {
      cancelled = true;
    };
  }, [isUserMode]);

  useEffect(() => {
    if (isUserMode) return undefined;
    const loadUsers = async () => {
      setLoadingUsers(true);
      setError('');
      try {
        const data = await getUsers(1, 200);
        const list = (data?.data || []).filter((u) => u.phone_number_id && u.waba_id);
        setUsers(list);
      } catch (e) {
        setError(e?.response?.data?.message || e?.message || 'Failed to load users');
      } finally {
        setLoadingUsers(false);
      }
    };
    loadUsers();
    return undefined;
  }, [isUserMode]);

  const catalogRequestOptions = () => {
    if (isUserMode) return {};
    if (!selectedUserId) return null;
    return { target_user_id: selectedUserId };
  };

  const loadCatalogs = async (catalogId) => {
    const options = catalogRequestOptions();
    if (options === null) return;
    if (isUserMode && !ownPhoneNumberId) return;
    setLoadingCatalogs(true);
    setError('');
    setMessage('');
    setWarnings([]);
    setCatalogs([]);
    setSelectedCatalogId('');
    setProducts([]);
    setNextCursor(null);
    setEditingProductId(null);
    setForm(emptyProductForm);
    try {
      const data = await getWhatsAppCatalogs(catalogId, options);
      const list = data?.data || [];
      setCatalogs(list);
      setWarnings(data?.warnings || []);
      setCommerceSettings(data?.commerce_settings || null);
      if (data?.commerce_settings?.is_catalog_visible && list.length === 0) {
        setMessage(
          'WhatsApp shows catalog as visible, but Graph returned no linked catalogs. Paste your Commerce Manager catalog ID below and fetch again.'
        );
      }
      if (list.length === 1) {
        setSelectedCatalogId(String(list[0].id));
      } else if (catalogId && list.some((c) => String(c.id) === String(catalogId))) {
        setSelectedCatalogId(String(catalogId));
      }
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || 'Failed to load catalogs');
    } finally {
      setLoadingCatalogs(false);
    }
  };

  const loadProducts = async (catalogId, after) => {
    const options = catalogRequestOptions();
    if (options === null || !catalogId) return;
    if (isUserMode && !ownPhoneNumberId) return;
    setLoadingProducts(true);
    setError('');
    try {
      const data = await getWhatsAppCatalogProducts(catalogId, after, options);
      const rows = data?.data || [];
      setProducts((prev) => (after ? [...prev, ...rows] : rows));
      setNextCursor(data?.paging?.cursors?.after || null);
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || 'Failed to load products');
    } finally {
      setLoadingProducts(false);
    }
  };

  useEffect(() => {
    if (isUserMode) {
      if (!ownPhoneNumberId) {
        setCatalogs([]);
        setSelectedCatalogId('');
        setProducts([]);
        setWarnings([]);
        setCommerceSettings(null);
        return;
      }
      loadCatalogs();
      return;
    }
    if (!selectedUserId) {
      setCatalogs([]);
      setSelectedCatalogId('');
      setProducts([]);
      setWarnings([]);
      setCommerceSettings(null);
      return;
    }
    loadCatalogs();
  }, [isUserMode, ownPhoneNumberId, selectedUserId]);

  useEffect(() => {
    const canLoad = isUserMode ? Boolean(ownPhoneNumberId) : Boolean(selectedUserId);
    if (!canLoad || !selectedCatalogId) {
      setProducts([]);
      setNextCursor(null);
      return;
    }
    loadProducts(selectedCatalogId);
  }, [isUserMode, ownPhoneNumberId, selectedUserId, selectedCatalogId]);

  const onFormChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const onFetchManualCatalog = async () => {
    if (isUserMode && !ownPhoneNumberId) {
      setError('Your phone number ID is missing.');
      return;
    }
    if (!isUserMode && !selectedUserId) {
      setError('Select a user first.');
      return;
    }
    if (!manualCatalogId.trim()) {
      setError('Enter a Commerce Manager catalog ID.');
      return;
    }
    await loadCatalogs(manualCatalogId.trim());
  };

  const resetForm = () => {
    setEditingProductId(null);
    setForm(emptyProductForm);
    if (localPreviewUrl) {
      URL.revokeObjectURL(localPreviewUrl);
      setLocalPreviewUrl('');
    }
  };

  const onEditProduct = (product) => {
    setEditingProductId(product.id);
    setForm(productToForm(product));
    if (localPreviewUrl) {
      URL.revokeObjectURL(localPreviewUrl);
      setLocalPreviewUrl('');
    }
    setError('');
    setMessage('');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const onImageFileChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setError('Please choose an image file (JPG, PNG, WebP, or GIF).');
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      setError('Image must be 8 MB or smaller.');
      return;
    }

    setError('');
    setMessage('');
    if (localPreviewUrl) URL.revokeObjectURL(localPreviewUrl);
    const preview = URL.createObjectURL(file);
    setLocalPreviewUrl(preview);
    setUploadingImage(true);
    try {
      const data = await uploadWhatsAppCatalogImage(file);
      if (!data?.image_url) throw new Error('Upload did not return an image URL');
      onFormChange('image_url', data.image_url);
      setMessage('Image uploaded. It will be attached when you save the product.');
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || 'Failed to upload image');
      URL.revokeObjectURL(preview);
      setLocalPreviewUrl('');
    } finally {
      setUploadingImage(false);
    }
  };

  const onSaveProduct = async () => {
    setError('');
    setMessage('');
    if ((!isUserMode && !selectedUserId) || !selectedCatalogId) {
      setError(isUserMode ? 'Select a catalog first.' : 'Select a user and catalog first.');
      return;
    }
    if (!form.retailer_id || !form.name || !form.price) {
      setError('Retailer ID (Content ID), name, and price are required.');
      return;
    }
    setSubmitting(true);
    try {
      const options = catalogRequestOptions() || {};
      if (editingProductId) {
        await updateWhatsAppCatalogProduct({
          product_id: editingProductId,
          ...form,
          ...options,
        });
        setMessage('Product updated.');
      } else {
        await createWhatsAppCatalogProduct({
          catalog_id: selectedCatalogId,
          ...form,
          ...options,
        });
        setMessage('Product added to catalogue.');
      }
      resetForm();
      await loadProducts(selectedCatalogId);
    } catch (e) {
      setError(
        e?.response?.data?.message ||
          e?.message ||
          (editingProductId ? 'Failed to update product' : 'Failed to create product')
      );
    } finally {
      setSubmitting(false);
    }
  };

  const onDeleteProduct = async (product) => {
    if (!product?.id) return;
    if (!isUserMode && !selectedUserId) return;
    const label = product.name || product.retailer_id || product.id;
    if (!window.confirm(`Delete product “${label}” from Meta catalogue?`)) return;
    setDeletingId(product.id);
    setError('');
    setMessage('');
    try {
      await deleteWhatsAppCatalogProduct(product.id, catalogRequestOptions() || {});
      setMessage('Product deleted.');
      if (editingProductId === product.id) resetForm();
      await loadProducts(selectedCatalogId);
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || 'Failed to delete product');
    } finally {
      setDeletingId('');
    }
  };

  const showAccountMeta = isUserMode ? Boolean(phoneNumberId) : Boolean(selectedUser);
  const wabaDisplay = isUserMode
    ? accountMeta?.waba_id || '—'
    : selectedUser?.waba_id || '—';

  return (
    <div className="admin-page">
      <header className="admin-page__hero">
        <div className="admin-page__title-row">
          <div className="admin-page__icon" aria-hidden>
            <FontAwesomeIcon icon={faStore} />
          </div>
          <div>
            <span className="admin-page__eyebrow">
              {isUserMode ? 'Catalogue' : 'Administration'}
            </span>
            <h1 className="admin-page__title">
              {isUserMode ? 'My Catalogue' : 'Catalogue'}
            </h1>
            <p className="admin-page__subtitle">
              {isUserMode
                ? 'Manage products in your WhatsApp / Meta catalogue — view, add, edit, and delete items.'
                : 'Manage WhatsApp / Meta catalogue products — view, add, edit, and delete items.'}
            </p>
          </div>
        </div>
      </header>

      <section className="admin-panel">
        <div className="admin-panel__head">
          <h2>{isUserMode ? 'Your catalog' : 'Account & catalog'}</h2>
          <p>
            {isUserMode
              ? 'Catalogues linked to your WhatsApp Business account.'
              : 'Choose a user, then pick a Meta catalogue to manage products.'}
          </p>
        </div>
        <div className="admin-panel__body">
          {error && (
            <div className="admin-feedback admin-feedback--error" style={{ marginBottom: 16 }}>
              {error}
            </div>
          )}
          {message && (
            <div className="admin-feedback admin-feedback--success" style={{ marginBottom: 16 }}>
              {message}
            </div>
          )}
          {warnings.length > 0 && (
            <div className="admin-feedback admin-feedback--error" style={{ marginBottom: 16 }}>
              {warnings.map((w, i) => (
                <div key={i} style={{ marginBottom: i === warnings.length - 1 ? 0 : 8 }}>
                  {w}
                </div>
              ))}
            </div>
          )}

          <div className="admin-grid">
            {!isUserMode && (
              <div className="admin-field">
                <span className="admin-label">User account</span>
                <select
                  className="admin-select"
                  value={selectedUserId}
                  disabled={loadingUsers}
                  onChange={(e) => setSelectedUserId(e.target.value)}
                >
                  <option value="">{loadingUsers ? 'Loading…' : 'Select user'}</option>
                  {users.map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.name || u.email} — {u.phone_number_id}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div className="admin-field">
              <span className="admin-label">Catalog</span>
              <select
                className="admin-select"
                value={selectedCatalogId}
                disabled={
                  (isUserMode ? !ownPhoneNumberId : !selectedUserId) ||
                  loadingCatalogs ||
                  loadingAccount ||
                  catalogs.length === 0
                }
                onChange={(e) => {
                  setSelectedCatalogId(e.target.value);
                  resetForm();
                }}
              >
                <option value="">
                  {loadingAccount || loadingCatalogs
                    ? 'Loading catalogs…'
                    : catalogs.length
                      ? 'Select catalog'
                      : 'No catalogs found'}
                </option>
                {catalogs.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name || c.id}
                    {c.product_count != null ? ` (${c.product_count})` : ''}
                  </option>
                ))}
              </select>
            </div>

            <div className="admin-field">
              <span className="admin-label">Commerce Manager catalog ID</span>
              <input
                className="admin-input"
                value={manualCatalogId}
                onChange={(e) => setManualCatalogId(e.target.value)}
                placeholder="e.g. 903312529006588"
              />
            </div>
          </div>

          {showAccountMeta && (
            <div className="admin-catalog-meta">
              <div><strong>WABA ID:</strong> {wabaDisplay}</div>
              <div><strong>Phone number ID:</strong> {phoneNumberId || '—'}</div>
              {isUserMode && accountMeta?.phone_number && (
                <div><strong>Phone:</strong> {accountMeta.phone_number}</div>
              )}
              {selectedCatalog && (
                <>
                  <div><strong>Catalogue:</strong> {selectedCatalog.name || '—'}</div>
                  <div><strong>Catalogue ID:</strong> {selectedCatalog.id}</div>
                  <div><strong>Products:</strong> {selectedCatalog.product_count ?? '—'}</div>
                  <div><strong>Vertical:</strong> {selectedCatalog.vertical || '—'}</div>
                </>
              )}
              {commerceSettings && (
                <>
                  <div>
                    <strong>Cart enabled:</strong>{' '}
                    {commerceSettings.is_cart_enabled ? 'Yes' : 'No'}
                  </div>
                  <div>
                    <strong>Catalog visible:</strong>{' '}
                    {commerceSettings.is_catalog_visible ? 'Yes' : 'No'}
                  </div>
                </>
              )}
            </div>
          )}

          <div style={{ marginTop: 12, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button
              type="button"
              className="admin-btn admin-btn--primary"
              disabled={(isUserMode ? !ownPhoneNumberId : !selectedUserId) || loadingCatalogs || loadingAccount || !manualCatalogId.trim()}
              onClick={onFetchManualCatalog}
            >
              Fetch / link catalog ID
            </button>
            <button
              type="button"
              className="admin-btn admin-btn--ghost"
              disabled={(isUserMode ? !ownPhoneNumberId : !selectedUserId) || loadingCatalogs || loadingAccount}
              onClick={() => loadCatalogs()}
            >
              <FontAwesomeIcon icon={faRotateRight} /> Refresh catalogs
            </button>
            <button
              type="button"
              className="admin-btn admin-btn--ghost"
              disabled={(isUserMode ? !ownPhoneNumberId : !selectedUserId) || !selectedCatalogId || loadingProducts}
              onClick={() => loadProducts(selectedCatalogId)}
            >
              <FontAwesomeIcon icon={faRotateRight} /> Refresh products
            </button>
          </div>
        </div>
      </section>

      <section className="admin-panel">
        <div className="admin-panel__head">
          <h2>{editingProductId ? 'Edit product' : 'Add product'}</h2>
          <p>
            {editingProductId
              ? `Editing Meta product ${editingProductId}`
              : 'Creates a product in the selected Meta catalogue.'}
          </p>
        </div>
        <div className="admin-panel__body">
          <div className="admin-grid">
            <div className="admin-field">
              <span className="admin-label">Content / Retailer ID *</span>
              <input
                className="admin-input"
                value={form.retailer_id}
                onChange={(e) => onFormChange('retailer_id', e.target.value)}
                placeholder="e.g. bette1"
              />
            </div>
            <div className="admin-field">
              <span className="admin-label">Name *</span>
              <input
                className="admin-input"
                value={form.name}
                onChange={(e) => onFormChange('name', e.target.value)}
                placeholder="Product name"
              />
            </div>
            <div className="admin-field">
              <span className="admin-label">Price *</span>
              <input
                className="admin-input"
                value={form.price}
                onChange={(e) => onFormChange('price', e.target.value)}
                placeholder="e.g. 59000.00"
              />
            </div>
            <div className="admin-field">
              <span className="admin-label">Currency</span>
              <input
                className="admin-input"
                value={form.currency}
                onChange={(e) => onFormChange('currency', e.target.value.toUpperCase())}
                placeholder="INR"
              />
            </div>
            <div className="admin-field">
              <span className="admin-label">Availability</span>
              <select
                className="admin-select"
                value={form.availability}
                onChange={(e) => onFormChange('availability', e.target.value)}
              >
                {AVAILABILITY_OPTIONS.map((opt) => (
                  <option key={opt} value={opt}>{opt}</option>
                ))}
              </select>
            </div>
            <div className="admin-field">
              <span className="admin-label">Condition</span>
              <select
                className="admin-select"
                value={form.condition}
                onChange={(e) => onFormChange('condition', e.target.value)}
              >
                {CONDITION_OPTIONS.map((opt) => (
                  <option key={opt} value={opt}>{opt}</option>
                ))}
              </select>
            </div>
            <div className="admin-field">
              <span className="admin-label">Brand</span>
              <input
                className="admin-input"
                value={form.brand}
                onChange={(e) => onFormChange('brand', e.target.value)}
              />
            </div>
            <div className="admin-field" style={{ gridColumn: '1 / -1' }}>
              <span className="admin-label">Product image</span>
              <div className="admin-image-upload">
                <label className="admin-btn admin-btn--ghost admin-file-label">
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/webp,image/gif"
                    onChange={onImageFileChange}
                    disabled={uploadingImage || submitting}
                  />
                  {uploadingImage ? 'Uploading…' : 'Upload image'}
                </label>
                <input
                  className="admin-input"
                  value={form.image_url}
                  onChange={(e) => onFormChange('image_url', e.target.value)}
                  placeholder="Or paste image URL https://…"
                  disabled={uploadingImage}
                />
              </div>
              <span className="admin-hint">
                Upload a JPG/PNG (max 8 MB), or paste a public image URL. Meta needs a reachable URL.
              </span>
            </div>
            <div className="admin-field">
              <span className="admin-label">Product URL</span>
              <input
                className="admin-input"
                value={form.url}
                onChange={(e) => onFormChange('url', e.target.value)}
                placeholder="https://…"
              />
            </div>
            <div className="admin-field" style={{ gridColumn: '1 / -1' }}>
              <span className="admin-label">Description</span>
              <textarea
                className="admin-input"
                rows={3}
                value={form.description}
                onChange={(e) => onFormChange('description', e.target.value)}
              />
            </div>
          </div>
          {(localPreviewUrl || form.image_url) ? (
            <div className="admin-product-preview">
              <img src={localPreviewUrl || form.image_url} alt="" />
            </div>
          ) : null}
          <div style={{ marginTop: 16, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button
              type="button"
              className="admin-btn admin-btn--primary"
              disabled={submitting || uploadingImage || !selectedCatalogId}
              onClick={onSaveProduct}
            >
              <FontAwesomeIcon icon={editingProductId ? faPen : faPlus} />
              {submitting
                ? editingProductId
                  ? 'Saving…'
                  : 'Adding…'
                : uploadingImage
                  ? 'Wait for upload…'
                  : editingProductId
                    ? 'Save changes'
                    : 'Add product'}
            </button>
            {editingProductId && (
              <button
                type="button"
                className="admin-btn admin-btn--ghost"
                disabled={submitting}
                onClick={resetForm}
              >
                <FontAwesomeIcon icon={faXmark} /> Cancel edit
              </button>
            )}
          </div>
        </div>
      </section>

      <section className="admin-panel">
        <div className="admin-panel__head">
          <h2>Products</h2>
          <p>
            {selectedCatalog
              ? `${products.length} loaded${nextCursor ? ' (more available)' : ''}`
              : 'Select a catalog to view products'}
          </p>
        </div>
        <div className="admin-panel__body admin-panel__body--flush">
          <div className="admin-table-wrap">
            <table className="admin-table admin-table--catalog">
              <thead>
                <tr>
                  <th>Image</th>
                  <th>Content ID</th>
                  <th>Name</th>
                  <th>Price</th>
                  <th>Currency</th>
                  <th>Availability</th>
                  <th>Condition</th>
                  <th>Brand</th>
                  <th>Meta ID</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {!selectedCatalogId ? (
                  <tr>
                    <td colSpan={10} className="admin-empty">
                      Select a catalog to load products.
                    </td>
                  </tr>
                ) : loadingProducts && products.length === 0 ? (
                  <tr>
                    <td colSpan={10} className="admin-empty">
                      Loading products…
                    </td>
                  </tr>
                ) : products.length === 0 ? (
                  <tr>
                    <td colSpan={10} className="admin-empty">
                      No products in this catalog.
                    </td>
                  </tr>
                ) : (
                  products.map((p) => (
                    <tr
                      key={p.id}
                      className={editingProductId === p.id ? 'is-editing' : undefined}
                    >
                      <td>
                        {p.image_url ? (
                          <img className="admin-product-thumb" src={p.image_url} alt="" />
                        ) : (
                          '—'
                        )}
                      </td>
                      <td className="admin-cell-clamp">{p.retailer_id || '—'}</td>
                      <td>
                        <div className="admin-cell-clamp">{p.name || '—'}</div>
                        {p.description ? (
                          <div className="admin-cell-sub admin-cell-clamp">{p.description}</div>
                        ) : null}
                      </td>
                      <td>{p.price || '—'}</td>
                      <td>{p.currency || '—'}</td>
                      <td>{p.availability || '—'}</td>
                      <td>{p.condition || '—'}</td>
                      <td className="admin-cell-clamp">{p.brand || '—'}</td>
                      <td className="admin-cell-clamp">{p.id}</td>
                      <td>
                        <div style={{ display: 'flex', gap: 6 }}>
                          <button
                            type="button"
                            className="admin-btn admin-btn--ghost admin-btn--sm"
                            onClick={() => onEditProduct(p)}
                            disabled={submitting}
                          >
                            <FontAwesomeIcon icon={faPen} />
                          </button>
                          <button
                            type="button"
                            className="admin-btn admin-btn--danger admin-btn--sm"
                            onClick={() => onDeleteProduct(p)}
                            disabled={deletingId === p.id || submitting}
                          >
                            <FontAwesomeIcon icon={faTrash} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          {nextCursor ? (
            <div style={{ padding: 16 }}>
              <button
                type="button"
                className="admin-btn admin-btn--ghost"
                disabled={loadingProducts}
                onClick={() => loadProducts(selectedCatalogId, nextCursor)}
              >
                {loadingProducts ? 'Loading…' : 'Load more'}
              </button>
            </div>
          ) : null}
        </div>
      </section>
    </div>
  );
};

export default ManageCatalogue;
